/**
 * Users API — TanStack Query factories.
 *
 * Phase-2 Wave-3 W3.T1. Wraps the Phase-1 `apiFetch` chokepoint with the
 * query/mutation primitives the Employees page (W3.T2) and detail modal
 * (Wave 4) consume. Three guiding principles:
 *
 *   1. **Stable query keys**. Every list/detail key flows from the shared
 *      `qk.users.*` factory in `lib/query-keys.ts`. Mutations invalidate
 *      `qk.users.all` so every list view (Active, Pending, Archive) plus
 *      the detail panel refetch in lock-step — no stale rows after an
 *      archive / role-change / re-issue.
 *
 *   2. **Server-shape parity**. The list query returns the API's
 *      `UserListResponse` shape verbatim (rows + page + totalRows +
 *      totalPages) so the page component can hand it straight to the
 *      `<DataTable>` + `<Pagination>` primitives without an adapter.
 *
 *   3. **No silent mutation success**. Every mutation also seeds the
 *      detail-cache when the server payload supplies the fresh row, so
 *      a follow-up `useUserQuery(id)` after `useUpdateUserMutation`
 *      hydrates instantly without a round-trip.
 *
 * Endpoints (mounted under `/api/v1`):
 *   GET    /users                    — list with filters + pagination
 *   GET    /users/:id                — single user
 *   PATCH  /users/:id                — update (optimistic-lock via `version`)
 *   POST   /users/:id/archive        — archive
 *   POST   /users/:id/reissue-invite — re-create invite (PENDING only)
 *
 * Returned envelopes are typed against `@aris/shared` so any divergence
 * between the BE contract and FE consumer surfaces as a TS error.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { AccessLevel, RoleSlug, UserStatus } from '@aris/shared';
import type {
  ListUsersQuery,
  UpdateUserRequest,
  UserDetail,
  UserListResponse,
  UserSummary,
  UserWorkloadListResponse,
} from '@aris/shared';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Build a `?key=value&…` querystring from a structured filter, dropping
 * `undefined` / empty-string entries so the URL stays clean and the BE
 * sees only fields the user actually set. Numbers are coerced to strings
 * (URLSearchParams won't accept numbers in TS).
 */
export function buildUserListSearch(query: ListUsersQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (typeof query.departmentId === 'number') {
    params.set('departmentId', String(query.departmentId));
  }
  if (query.roleSlug) params.set('roleSlug', query.roleSlug);
  if (query.accessLevel) params.set('accessLevel', query.accessLevel);
  if (query.search && query.search.length > 0) params.set('search', query.search);
  if (query.cursor) params.set('cursor', query.cursor);
  if (typeof query.page === 'number') params.set('page', String(query.page));
  if (typeof query.limit === 'number') params.set('limit', String(query.limit));
  // Ф8 · В-З1/В-З2 — назначение списка. Сегодня значение одно, `workload`:
  // список выбора сотрудника на странице «Загруженность».
  if (query.scope) params.set('scope', query.scope);
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

// ─── List ────────────────────────────────────────────────────────────────

/**
 * `GET /users` — paginated, filtered. Pass exactly the fields the user
 * has set; defaults (`page=1`, `limit=50`) come from the shared schema.
 *
 * The `enabled` flag lets the page disable the query entirely (e.g. if
 * the user lands on /employees while their role briefly resolves to one
 * that can't see the page — `RequireAccessLevel` is the firm gate, but
 * skipping the fetch avoids a transient 403 toast).
 */
export function useUsersQuery(query: ListUsersQuery, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery<UserListResponse>({
    queryKey: qk.users.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => apiFetch<UserListResponse>(`/users${buildUserListSearch(query)}`),
    enabled,
    // Keeping previous data while a new page/filter loads keeps the table
    // visible (instead of flashing the loading state on every keystroke).
    placeholderData: (prev) => prev,
  });
}

/**
 * `GET /users?scope=workload` — список выбора сотрудника на странице
 * «Загруженность» (Блок 2 · Ф8 · В-З2; договор — волна В-З1).
 *
 * ⚠️ ОТДЕЛЬНЫЙ ХУК, А НЕ ПАРАМЕТР `useUsersQuery`, ПОТОМУ ЧТО ОТВЕТ ДРУГОЙ
 * ФОРМЫ. С признаком сервер отдаёт руководителю отдела, которому добавлено
 * право «вся загруженность бюро», людей ВСЕХ отделов — но чужих узкой строкой
 * (`UserPickerRow`: `id`, `fullName`, `departmentId`; ни почты, ни телефона).
 * Тип `UserListResponse` обещал бы потребителю почту, которой в такой строке
 * нет. Всем остальным признак безвреден: без права он инертен, у
 * администратора строки полные.
 *
 * ⚠️ ПРИЗНАК СТАВИТ ХУК, А НЕ ВЫЗЫВАЮЩИЙ: забыть его нельзя. Он же попадает в
 * ключ запроса — выдача списка выбора не делит кэш со справочником
 * сотрудников.
 *
 * Сотруднику сервер отвечает на этот запрос 403 — вызывающий выключает его
 * флагом `enabled` (выбора сотрудника у него нет).
 */
export function useWorkloadPickerUsersQuery(
  query: Omit<ListUsersQuery, 'scope'>,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const scoped: ListUsersQuery = { ...query, scope: 'workload' };
  return useQuery<UserWorkloadListResponse>({
    queryKey: qk.users.list(scoped as Readonly<Record<string, unknown>>),
    queryFn: () => apiFetch<UserWorkloadListResponse>(`/users${buildUserListSearch(scoped)}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/**
 * `GET /users?accessLevel=ADMIN&status=ACTIVE&limit=100` — feeds the
 * supervisor-only "Emergency Admin recovery" combobox.
 *
 * The accessLevel filter is enforced server-side (defense in depth: the
 * BE service joins through `Role.accessLevel`); the FE sends it explicitly
 * so the result is narrow even when the BE briefly serves an older index.
 *
 * `enabled` defaults to `true`; callers pass `false` while the page guard
 * is still resolving the supervisor identity (avoids a brief 403 toast
 * when role briefly transitions during cold-boot).
 */
export function useAdminsListQuery(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const query: ListUsersQuery = {
    accessLevel: AccessLevel.ADMIN,
    status: UserStatus.ACTIVE,
    page: 1,
    limit: 100,
  };
  return useQuery<UserListResponse>({
    queryKey: qk.users.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => apiFetch<UserListResponse>(`/users${buildUserListSearch(query)}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/**
 * Feature 1 «Помощник ГИПа» (Wave 5). Lists active GIP-role users to feed
 * the GIP-picker shown when an ADMIN/SUPERVISOR inviter (who is NOT
 * themselves a GIP) creates / converts an ASSISTANT_GIP. Filters by the
 * canonical role slug (`GIP`) + ACTIVE status server-side — non-archived,
 * non-pending — so the picker only offers valid bind targets (the BE
 * `assertValidGipTarget` re-checks on submit). We request a generous page
 * size (100) because this is a 14-user internal tool; pagination is never
 * needed here in practice.
 *
 * `enabled` lets the modal skip the fetch entirely unless the GIP-picker is
 * actually shown (ASSISTANT_GIP selected AND the actor is not a GIP),
 * avoiding a needless round-trip on every modal open.
 */
export function useGipUsersQuery(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const query: ListUsersQuery = {
    roleSlug: RoleSlug.GIP,
    status: UserStatus.ACTIVE,
    page: 1,
    limit: 100,
  };
  return useQuery<UserListResponse>({
    queryKey: qk.users.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => apiFetch<UserListResponse>(`/users${buildUserListSearch(query)}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

// ─── Detail ──────────────────────────────────────────────────────────────

export function useUserQuery(id: string | undefined) {
  return useQuery<UserDetail>({
    queryKey: id ? qk.users.detail(id) : ['users', 'detail', '__pending__'],
    queryFn: () => apiFetch<UserDetail>(`/users/${id}`),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

/**
 * Блок 2 · Ф8 · В-Э. Запись в человека меняет и его права, хотя маршрут — не
 * про права: смена должности, возврат в приглашённые и повторное приглашение
 * СБРАСЫВАЮТ всё добавленное (Р-198; решение плана П3), архивация
 * делает строки недействующими, удаление приглашённого убирает их вовсе.
 * Карточка прав и сводный список после этого устарели — сбрасываются оба.
 *
 * Неактивные запросы только помечаются устаревшими и сеть не трогают: у
 * администратора и руководителя отдела этих ключей в кэше нет вовсе.
 */
function invalidateUserRights(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.userRights.all });
}

interface UpdateUserVars {
  id: string;
  patch: UpdateUserRequest;
}

/**
 * `PATCH /users/:id` — optimistic-lock via `version` (Phase 1 T11.1).
 * On success we both seed the detail cache with the fresh row and bust
 * every list query so filters / pagination / status-tabs reflect the
 * new state without a stale window.
 */
export function useUpdateUserMutation() {
  const qc = useQueryClient();
  return useMutation<UserSummary, unknown, UpdateUserVars>({
    mutationFn: ({ id, patch }) =>
      apiFetch<UserSummary>(`/users/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (fresh, vars) => {
      qc.setQueryData(qk.users.detail(vars.id), fresh);
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
      invalidateUserRights(qc);
    },
  });
}

interface ArchiveUserVars {
  id: string;
}

/**
 * `POST /users/:id/archive`. Backend enforces the last-admin guard +
 * cascades open invites/resets. We invalidate every users-* key so the
 * row falls out of the Active tab AND appears in the Archive tab on the
 * next render.
 */
export function useArchiveUserMutation() {
  const qc = useQueryClient();
  return useMutation<UserSummary, unknown, ArchiveUserVars>({
    mutationFn: ({ id }) => apiFetch<UserSummary>(`/users/${id}/archive`, { method: 'POST' }),
    onSuccess: (fresh, vars) => {
      qc.setQueryData(qk.users.detail(vars.id), fresh);
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
      invalidateUserRights(qc);
    },
  });
}

interface HardDeleteUserVars {
  id: string;
}

/**
 * `DELETE /users/:id` — hard-delete a PENDING user (Phase 2 W1.T8).
 *
 * Only valid for users with `status === 'PENDING'`; the BE returns 422
 * `cannot_hard_delete_active` for any other status. RBAC mirrors archive
 * (Admin handles Admin/РО tiers, РО handles own-dept Сотрудники). The
 * server cascades open invites + tokens in the same transaction so the
 * row + every referencing record is gone after a 204.
 *
 * On success we both drop the per-row detail cache (the row no longer
 * exists, so any future detail-by-id query should refetch and 404) and
 * invalidate `qk.users.all` so the Pending tab + counts update without
 * a stale window.
 */
export function useHardDeleteUserMutation() {
  const qc = useQueryClient();
  return useMutation<void, unknown, HardDeleteUserVars>({
    // 204 No Content — apiFetch resolves to undefined for empty 2xx
    // responses, which is what we want here.
    mutationFn: ({ id }) => apiFetch<void>(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: (_void, vars) => {
      // The detail cache is now stale by construction — the row is gone.
      qc.removeQueries({ queryKey: qk.users.detail(vars.id) });
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
      invalidateUserRights(qc);
    },
  });
}

export interface ReissueInviteResult {
  inviteUrl: string;
  expiresAt: string;
}

interface ReissueInviteVars {
  id: string;
}

/**
 * `POST /users/:id/reissue-invite` — generates a new invite token for a
 * PENDING user and returns the freshly-signed URL. We invalidate the
 * list because the user's `updatedAt` shifts and the Pending-tab row
 * may move within sort order; we don't seed the detail cache (the
 * archived/active membership doesn't change here).
 */
export function useReissueInviteMutation() {
  const qc = useQueryClient();
  return useMutation<ReissueInviteResult, unknown, ReissueInviteVars>({
    mutationFn: ({ id }) =>
      apiFetch<ReissueInviteResult>(`/users/${id}/reissue-invite`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
      invalidateUserRights(qc);
    },
  });
}
